import dynamic from 'next/dynamic';
import type { ComponentType, HTMLAttributes } from 'react';
import type { LogoProps } from './types';
import { LogoWrapper } from './ThirdPartyLogo.styles';
import { logoMap } from './map';
import AvisLogo from './assets/Avis';
import BudgetLogo from './assets/Budget';
import EnterpriseLogo from './assets/Enterprise';
import EuropcarLogo from './assets/Europcar';
import HertzLogo from './assets/Hertz';

type Props = {
  name: string;
  grayScale?: boolean;
} & Pick<LogoProps, 'withPadding'> &
  Pick<HTMLAttributes<HTMLDivElement>, 'className'>;

// Logos used above the fold. They are in the main bundle.
const staticLogos: Record<string, ComponentType<LogoProps>> = {
  Avis: AvisLogo,
  Budget: BudgetLogo,
  Enterprise: EnterpriseLogo,
  Europcar: EuropcarLogo,
  Hertz: HertzLogo,
};

// Each logo is a separate chunk. The `dynamic()` calls must stay at module
// level: a call during the render makes a new component type, so React mounts
// the logo again on each render, and the server does not render the logo.
const lazyLogos: Record<string, ComponentType<LogoProps>> = {
  Alamo: dynamic(() => import('./assets/Alamo')),
  ArgusCarHire: dynamic(() => import('./assets/ArgusCarHire')),
  Autoeurope: dynamic(() => import('./assets/Autoeurope')),
  Buchbinder: dynamic(() => import('./assets/Buchbinder')),
  Dollar: dynamic(() => import('./assets/Dollar')),
  Global: dynamic(() => import('./assets/Global')),
  Interrent: dynamic(() => import('./assets/Interrent')),
  Keddy: dynamic(() => import('./assets/Keddy')),
  National: dynamic(() => import('./assets/National')),
  RecordGo: dynamic(() => import('./assets/RecordGo')),
  Rentalcars: dynamic(() => import('./assets/Rentalcars')),
  Sunnycars: dynamic(() => import('./assets/Sunnycars')),
  Thrifty: dynamic(() => import('./assets/Thrifty')),
  Wheego: dynamic(() => import('./assets/Wheego')),
};

/**
 * Loads a third party logo.
 * By default the logo is positioned in a view box of 137x49.
 * This can be adjusted using the `withPadding` prop.
 */
export const ThirdPartyLogo = ({
  name,
  withPadding = 'both',
  grayScale = false,
  className,
}: Props) => {
  const logoComponent = logoMap[name.toLowerCase()];
  const LogoComponent = logoComponent
    ? (staticLogos[logoComponent] ?? lazyLogos[logoComponent])
    : undefined;

  if (!LogoComponent) {
    return null;
  }

  return (
    <LogoWrapper grayScale={grayScale} className={className}>
      <LogoComponent withPadding={withPadding} />
    </LogoWrapper>
  );
};
